/**
 * CallTrackingMetrics Adapter — Story 22.3 (opens Epic 22 CTM slice)
 *
 * **Auth model:** Per-user **TWO secrets** in one credential bundle —
 * `Authorization: Basic <base64(ACCESS_KEY:SECRET_KEY)>` HTTP header.
 * The customer pastes BOTH the CTM Access Key AND the CTM Secret Key
 * (generated in the CTM API integrations page,
 * `app.calltrackingmetrics.com/accounts/setup/integrations`) into the
 * Connect dialog. We encrypt them SEPARATELY via the same
 * `encryptToken` helper that OAuth tokens use and store them in
 * `oauth_tokens` by overloading the column semantics:
 *
 *   - `access_token_encrypted = <encrypted ACCESS_KEY>`
 *   - `refresh_token_encrypted = <encrypted SECRET_KEY>` (despite the
 *     column name — there is NO refresh flow; the column is reused as
 *     a second-secret bucket).
 *
 * **CRITICAL — COLUMN-OVERLOAD STORAGE PATTERN (FIRST in our library):**
 * A future agent reading the schema MUST NOT misinterpret these
 * columns as OAuth — they are reused as `ACCESS_KEY` + `SECRET_KEY`
 * slots for the CTM family ONLY. See
 * `packages/integrations/src/oauth-families.ts` `OAUTH_FAMILIES["calltrackingmetrics"]`
 * comment block for the full rationale.
 *
 * **DELIBERATE divergences from CallRail (Story 22.1) — internalize
 * before reaching for the CallRail files as a copy template:**
 *
 *  1. **Dual-secret HTTP Basic auth.** CTM is the FIRST adapter in our
 *     library to require TWO secrets in one credential bundle. Ahrefs/
 *     Semrush/Moz/CallRail all used a single token. Verified
 *     `github.com/calltracking/calltracking.github.io/blob/master/api_users_guide.md`
 *     (`-u ${ACCESS_KEY}:${SECRET_KEY}` curl example) 2026-05-15.
 *
 *  2. **REST + JSON with mandatory `.json` path-suffix.** Every CTM v1
 *     call is `/api/v1/accounts/{id}/<endpoint>.json`. DIFFERENT from
 *     CallRail's `/v3/a/{id}/` shape — CTM paths require both
 *     `/api/v1/` AND `.json` suffix.
 *
 *  3. **Multi-account/agency-vs-account scope discovery.** Agency keys
 *     span sub-accounts (per Settings > Parent Settings > API
 *     Integration); Account keys lock to one (per Settings > Account
 *     Settings > API Integration). Adapter calls `GET /accounts.json`
 *     at connect time to discover accessible accounts and infers
 *     `keyScope: "agency" | "account"` from `allowedAccountIds.length`
 *     for telemetry. 2-step wizard mirrors CallRail 22.1 with the
 *     addition of dual-secret input.
 *
 *  4. **EU region toggle with runtime-probe.** `api.eu.calltrackingmetrics.com`
 *     is catalogue-claimed for GDPR data residency but NOT publicly
 *     confirmed by web search. Adapter treats EU as runtime-probed at
 *     connect time. **FIRST adapter in our library with a region
 *     selector.**
 *
 *  5. **per_page=150 hard ceiling.** CTM `/calls.json` enforces 150
 *     max per page (`400 "per_page limit is 150"` if exceeded).
 *     DIFFERENT from CallRail 22.1's 250. Adapter clamps client-side
 *     BEFORE building the URL.
 *
 * **Customer PII flows through `fetchMetrics`.** Like CallRail 22.1,
 * CTM's per-call records carry caller-identifying data in the DEFAULT
 * response (`caller_number`, `name`, `email`, `street`/`city`/`state`/
 * `postal_code`/`country`, `geo`, `transcription`, `summary`, etc.).
 * The `sanitizeCtmPii` helper at `ctm/pii.ts` MUST run on EVERY record
 * before the record enters any log emission, Sentry breadcrumb, or
 * queue-error payload (Round 3 Red Team Attack #1).
 *
 * **Audio (recording) URLs are stripped at the transformer**, not the
 * log boundary. CTM's `audio` field is a signed URL — if a buggy
 * widget echoed this cross-tenant, it would be a data leak. Story
 * 22.3 deliberately does NOT persist `audio` URLs; widgets that need
 * them request via a dedicated tenant-scoped route (deferred to Story
 * 22.5). Round 3 Red Team Attack #2.
 *
 * **Sale endpoint deferred to Story 22.5+.** Story 22.3 reads
 * `record.sale` opportunistically (Endpoint Group D per AC #4) when
 * CTM inlines it on the `/calls.json` response. The per-call sale
 * endpoint (`/calls/{id}/sale.json`) is N+1 expensive and deferred
 * to a future story (with either a batched enrichment pass or a
 * `call_updated` webhook receiver).
 *
 * **Conversation Intelligence + VoiceAI + Scorecards + AI Texting +
 * LeadReactors are paid CTM add-ons** per
 * `www.ctm.com/plans-pricing/` verified 2026-05-15. Story 22.3 ships:
 *  - CI entitlement probe at connect time (mirrors CallRail 22.1 +
 *    Moz 20.7 entitlement-gate pattern).
 *  - Dynamic `fields=...` URL-param computation that strips CI-gated
 *    fields when `metadata.conversationIntelligenceEntitled === false`.
 *  - `discoverEndpoints` returns `subscription_required` on the
 *    `conversation-intelligence` endpoint when not entitled.
 *
 * **NO row-budget circuit-breaker** (DIVERGENT from Moz 20.7's
 * 2K-rows/day, Semrush 20.4's 200K-units/day, Ahrefs 20.1's
 * 5M-units/day; matches CallRail 22.1's stance). CTM does NOT bill
 * per-row; only request-count (~300 req/min/account soft cap).
 *
 * **NO snapshot persistence** (DIVERGENT from Moz 20.7's
 * `moz_authority_snapshots` D1 table; matches CallRail 22.1's
 * stance). CTM's endpoints support native date-range filtering.
 *
 * **NO data-persistence ToS cap** (verified
 * `www.calltrackingmetrics.com/legal/data-protection-addendum-2025/`
 * 2026-05-15 has no equivalent to Semrush ToS §3.3; matches CallRail
 * 22.1's stance). Uses the global 7-day stale fallback.
 *
 * **NO entry in `API_KEY_PLATFORMS`** (per AC #11 — that set is
 * reserved for env-binding API-key platforms like
 * `"google-pagespeed"`). CTM is per-user-secret like Ahrefs +
 * Semrush + Moz + CallRail, NOT env-key.
 *
 * **VoiceAI usage is billed per-minute SEPARATELY** ($0.12/min after
 * 250 free min per
 * `www.calltrackingmetrics.com/features/lead-management/voiceai`
 * verified 2026-05-15) and counts SEPARATELY from API request-count
 * quotas. Story 22.3 only consumes READ endpoints; future Story 22.x
 * VoiceAI work will need separate quota tracking.
 *
 * **Future expansion (deferred to Story 22.5+ unless noted):** Sale
 * endpoint enrichment (`/calls/{id}/sale.json`) → 22.5+; Chat
 * conversations + Agent/Live Calls (`/users` `live_calls[]`) +
 * Touchpoints/Legs/Transfers + Triggers/Voice Menus + VoiceAI agents
 * + AI Texting + Scorecards + LeadReactors → 22.5+; Webhooks
 * (6 event types catalogued) → Story 22.6+. Adding new endpoints is
 * purely additive (new helpers in `api.ts` + new transformers +
 * `MetricDefinition` rows).
 */

import {
  adapterErr,
  adapterOk,
  type AccountInfo,
  type AdapterContext,
  type DimensionDefinition,
  type EndpointDiscoveryInfo,
  type IntegrationAdapter,
  type MetricDefinition,
  type OAuthCredentials,
} from "../adapter";
import type { Platform } from "@provenleap/db";
import { API_VERSIONS } from "../config";

import {
  discoverCtmAccounts,
  fetchAllCtmCalls,
  fetchAllCtmFormSubmissions,
  fetchAllCtmSmsMessages,
  fetchAllCtmTriggers,
  fetchAllCtmIntegrations,
  probeCtmConversationIntelligence,
  validateCtmConnection,
  type CtmAccount,
} from "./api";
import {
  CTM_API_BASE_US,
  CTM_API_BASE_EU,
  CTM_DEFAULT_REGION,
  isCtmRegion,
  type CtmRegion,
} from "./region";
import { CtmApiCallError } from "./errors";
import { sanitizeCtmPii } from "./pii";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "calltrackingmetrics" as const;

export const CTM_PLATFORM_VALUES = ["calltrackingmetrics"] as const;
export type CtmPlatform = (typeof CTM_PLATFORM_VALUES)[number];

export const CTM_API_VERSION = API_VERSIONS.calltrackingmetrics;

/**
 * Hours of staleness stamped on `metadata.dataFreshnessLagHours`. CTM
 * processes calls/SMS/forms near-real-time. Matches CallRail 22.1's
 * 1-hour stance.
 */
export const CTM_FRESHNESS_LAG_HOURS = 1;

/** Public re-exports of region base URLs from `region.ts`. */
export { CTM_API_BASE_US, CTM_API_BASE_EU };

// ============================================================================
// CTM_METRICS — 48 priority metrics (40 wire + 8 synthetic-aggregate)
// ============================================================================

/**
 * **Story 22.3 ships 40 of 74 catalogued CTM metrics** — the priority
 * subset spanning the 4 endpoint groups (Calls List + Form Submissions
 * + SMS + inline Sale).
 *
 * Groups:
 *  - Call Volume & Status (8 derived)
 *  - Duration & Timing (5)
 *  - Derived Quality Rates (5)
 *  - Quality & Likelihood (3)
 *  - Inline Sale / Conversion (6)
 *  - Conversation Intelligence — SUBSCRIPTION-GATED (4)
 *  - Form Submissions (4)
 *  - SMS (5)
 *
 * The remaining 34 metrics are tagged `gap` in the catalogue and stay
 * deferred to Story 22.4 / 22.5+ (per-call IDs/SIDs, Touchpoints/Legs/
 * Transfers, Agent Performance via /users live_calls[], full Sale
 * endpoint enrichment, Voicemail Transcription, Spotted Keywords,
 * Renewal/Minute costs per /numbers, Calls per Number, Hour-of-Day,
 * additional ID fields and CNAM lookup, Sale notes/none flags, Call
 * Path).
 *
 * **`apiField: "<derived>"` sentinel** marks metrics that are computed
 * client-side from `/calls.json` aggregation rather than mapped 1:1
 * to a wire field. The build-time `metric-allowlist.test.ts` exempts
 * `<derived>` rows from the per-endpoint field allowlist check.
 */
export const CTM_METRICS: ReadonlyArray<MetricDefinition> = [
  // ── Call Volume & Status (8 derived from /calls.json) ─────────────
  {
    key: "totalCalls",
    label: "Total Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
    apiField: "<derived>",
    description: "Total number of calls in the date range — the headline CTM metric.",
  },
  {
    key: "answeredCalls",
    label: "Answered Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "<derived>",
    description: "Count of calls where dial_status = completed.",
  },
  {
    key: "missedCalls",
    label: "Missed Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "<derived>",
    description: "Count of calls where dial_status = no-answer.",
  },
  {
    key: "busyCalls",
    label: "Busy Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of calls where dial_status = busy.",
  },
  {
    key: "failedCalls",
    label: "Failed Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of calls where dial_status = failed.",
  },
  {
    key: "voicemailCalls",
    label: "Voicemail Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of calls where dial_status = voicemail.",
  },
  {
    key: "newCallerCount",
    label: "New Caller Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description: "Count of calls where is_new_caller = true.",
  },
  {
    key: "excludedCalls",
    label: "Excluded Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of calls where excluded = true (filtered by tracking rules).",
  },

  // ── Duration & Timing (5 — per-call from /calls.json) ─────────────
  {
    key: "talkTime",
    label: "Talk Time",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "additive",
    category: "Duration",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "talk_time",
    description: "Seconds spent talking (excludes ring + hold time).",
  },
  {
    key: "ringTime",
    label: "Ring Time",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "additive",
    category: "Duration",
    compatibleWidgets: ["data-table"],
    apiField: "ring_time",
    description: "Seconds the phone rang before answer/voicemail.",
  },
  {
    key: "totalDuration",
    label: "Total Duration",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "additive",
    category: "Duration",
    compatibleWidgets: ["kpi", "data-table"],
    apiField: "duration",
    description: "Total call duration (ring + talk + hold) in seconds.",
  },
  {
    key: "holdTime",
    label: "Hold Time",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Duration",
    compatibleWidgets: ["data-table"],
    apiField: "hold_time",
    description: "Seconds the caller spent on hold.",
  },
  {
    key: "waitTime",
    label: "Wait Time",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Duration",
    compatibleWidgets: ["data-table"],
    apiField: "wait_time",
    description: "Seconds before the call was answered (includes ring time).",
  },

  // ── Derived Quality Rates (5 — client-side derived) ───────────────
  {
    key: "answerRate",
    label: "Answer Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "answeredCalls", denominator: "totalCalls" },
    category: "Quality Rates",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "answeredCalls / totalCalls — percentage of calls answered.",
    synthetic: true,
  },
  {
    key: "missRate",
    label: "Miss Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "missedCalls", denominator: "totalCalls" },
    category: "Quality Rates",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "missedCalls / totalCalls — percentage of calls missed.",
    synthetic: true,
  },
  {
    key: "voicemailRate",
    label: "Voicemail Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "voicemailCalls", denominator: "totalCalls" },
    category: "Quality Rates",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "voicemailCalls / totalCalls — percentage of calls reaching voicemail.",
    synthetic: true,
  },
  {
    key: "newCallerRate",
    label: "New Caller Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newCallerCount", denominator: "totalCalls" },
    category: "Quality Rates",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "newCallerCount / totalCalls — share of calls from first-time callers.",
    synthetic: true,
  },
  {
    key: "averageTalkTime",
    label: "Average Talk Time",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { numerator: "talkTime", denominator: "answeredCalls" },
    category: "Quality Rates",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "<derived>",
    description: "Average talk_time across answered calls.",
    synthetic: true,
  },

  // ── Quality & Likelihood (3) ──────────────────────────────────────
  {
    key: "likelihoodScore",
    label: "Likelihood Score",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio",
    category: "Quality",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "likelihood",
    description: "CTM AI lead-quality score (0-1). CI-gated.",
  },
  {
    key: "taggedCallsCount",
    label: "Tagged Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Quality",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description: "Count of calls with at least one tag in tag_list.",
  },
  {
    key: "redactedCallsCount",
    label: "Redacted Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Quality",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of calls where redacted = true (PII-scrubbed by CTM).",
  },

  // ── Inline Sale / Conversion (6 — opportunistic from record.sale) ─
  {
    key: "conversionsCount",
    label: "Conversions",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversion (inline)",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of calls where sale.conversion = true (inline-only — full coverage requires Story 22.5+ sale endpoint enrichment).",
    synthetic: true,
  },
  {
    key: "totalRevenue",
    label: "Total Revenue",
    type: "currency",
    dataType: "Currency",
    aggregationKind: "additive",
    category: "Conversion (inline)",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "<derived>",
    description:
      "Sum of sale.value across calls with an inline sale object (inline-only — full coverage requires Story 22.5+).",
    synthetic: true,
  },
  {
    key: "averageSaleValue",
    label: "Average Sale Value",
    type: "currency",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "totalRevenue", denominator: "conversionsCount" },
    category: "Conversion (inline)",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Average sale.value across calls with an inline sale object.",
    synthetic: true,
  },
  {
    key: "averageSaleScore",
    label: "Average Sale Score",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "conversionsCount" },
    category: "Conversion (inline)",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "Average sale.score (1-10) across calls with an inline sale object.",
    synthetic: true,
  },
  {
    key: "conversionRate",
    label: "Conversion Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "conversionsCount", denominator: "totalCalls" },
    category: "Conversion (inline)",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "conversionsCount / totalCalls (inline sale coverage only).",
    synthetic: true,
  },
  {
    key: "serviceRepCount",
    label: "Service Reps",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversion (inline)",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description: "Distinct sale.service_rep_name across calls with an inline sale object.",
    synthetic: true,
  },

  // ── Conversation Intelligence — SUBSCRIPTION-GATED (4) ────────────
  {
    key: "transcription",
    label: "Transcription",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    compatibleWidgets: ["data-table"],
    apiField: "transcription",
    description: "Full call transcript (PII-redacted at log boundary). CI-gated.",
  },
  {
    key: "transcriptionText",
    label: "Transcription Text",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    compatibleWidgets: ["data-table"],
    apiField: "transcription_text",
    description: "Plain-text transcription (PII-redacted at log boundary). CI-gated.",
  },
  {
    key: "callSummary",
    label: "Call Summary",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    compatibleWidgets: ["data-table"],
    apiField: "summary",
    description: "AI-generated call summary (PII-redacted at log boundary). CI-gated.",
  },
  {
    key: "agentInsights",
    label: "Agent Insights",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    compatibleWidgets: ["data-table"],
    apiField: "agent_insights",
    description: "AI-extracted agent insights flag. CI-gated.",
  },

  // ── Form Submissions (4 — from /forms.json) ───────────────────────
  {
    key: "totalFormSubmissions",
    label: "Total Form Submissions",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    apiField: "<derived>",
    description: "Total form submissions in the date range.",
  },
  {
    key: "formReactorConversions",
    label: "Form Reactor Conversions",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description:
      "Count of forms with triggered_by = form_reactor (plan-tier dependent; emits 0 when field absent).",
    synthetic: true,
  },
  {
    key: "formSubmissionTime",
    label: "Form Submission Time",
    type: "number",
    dataType: "Enum",
    category: "Form Submissions",
    compatibleWidgets: ["data-table"],
    apiField: "created_at",
    description: "Form submission timestamp (UTC).",
  },
  {
    key: "firstFormSubmissionsCount",
    label: "First-Time Form Submitters",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description: "Distinct email count grouped by first-occurrence-per-email.",
    synthetic: true,
  },

  // ── SMS (5 — from /sms.json) ──────────────────────────────────────
  {
    key: "totalSmsMessages",
    label: "Total SMS Messages",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "SMS",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "<derived>",
    description: "Total SMS messages (inbound + outbound) in the date range.",
  },
  {
    key: "inboundSmsMessages",
    label: "Inbound SMS",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "SMS",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of SMS messages with direction = inbound.",
  },
  {
    key: "outboundSmsMessages",
    label: "Outbound SMS",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "SMS",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of SMS messages with direction = outbound.",
  },
  {
    key: "smsConversationsCount",
    label: "SMS Conversations",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "SMS",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "<derived>",
    description: "Distinct caller_number across SMS records (phone-number threads).",
    synthetic: true,
  },
  {
    key: "averageSmsResponseTime",
    label: "Average SMS Response Time",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "inboundSmsMessages" },
    category: "SMS",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Per-conversation average time between an inbound SMS and the next outbound SMS.",
    synthetic: true,
  },

  // ── Story 22.4 synthetic-aggregates (8 — closes Epic 22 in full) ──
  // Computed at preset-expansion time from `_reportType: "calls-list"` /
  // `"form-submissions"` / `"sms"` records. Do NOT correspond to wire-
  // format CTM API fields — flagged `synthetic: true` with `apiField:
  // "<derived>"`. Mirrors the CallRail 22.2 + Moz 20.8 synthetic-aggregate
  // pattern (Round 4 Acceptance Auditor (a) — bumping catalogue 40 → 48).
  {
    key: "likelihoodAvg",
    label: "Likelihood Avg",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCalls" },
    category: "Quality",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description:
      "Average AI Likelihood score (0-1) across all calls-list records. Build-to-sell signal for the CTM Cross-Endpoint Scorecard preset.",
    synthetic: true,
  },
  {
    key: "likelihoodBucketHigh",
    label: "Likelihood — High",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Quality",
    compatibleWidgets: ["kpi", "pie-chart"],
    apiField: "<derived>",
    description:
      "Count of calls-list rows with likelihood >= 0.8. The top quartile of CTM's AI lead-quality distribution.",
    synthetic: true,
  },
  {
    key: "likelihoodBucketGood",
    label: "Likelihood — Good",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Quality",
    compatibleWidgets: ["kpi", "pie-chart"],
    apiField: "<derived>",
    description:
      "Count of calls-list rows with likelihood ∈ [0.6, 0.8).",
    synthetic: true,
  },
  {
    key: "likelihoodBucketFair",
    label: "Likelihood — Fair",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Quality",
    compatibleWidgets: ["kpi", "pie-chart"],
    apiField: "<derived>",
    description:
      "Count of calls-list rows with likelihood ∈ [0.4, 0.6).",
    synthetic: true,
  },
  {
    key: "likelihoodBucketLow",
    label: "Likelihood — Low",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Quality",
    compatibleWidgets: ["kpi", "pie-chart"],
    apiField: "<derived>",
    description:
      "Count of calls-list rows with likelihood < 0.4. The bottom quartile of CTM's AI lead-quality distribution.",
    synthetic: true,
  },
  {
    key: "omnichannelTouchTotal",
    label: "Omnichannel Touch Total",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Omnichannel",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "totalCalls + totalFormSubmissions + totalSmsMessages. The Story 22.4 build-to-sell preset aggregate — no competitor reporting tool exposes this cross-surface count in a single metric.",
    synthetic: true,
  },
  {
    key: "callsTrendDirection",
    label: "Calls Trend Direction",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call Volume",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description:
      "weekN.totalCalls - week1.totalCalls — positive = growing, negative = declining. Mirrors CallRail 22.2's callsTrendDirection.",
    synthetic: true,
  },
  {
    key: "bestCallDayOfWeek",
    label: "Best Call Day of Week",
    // NOTE: `type: "number"` + `dataType: "Enum"` is the established
    // codebase convention for string-valued enum-style metrics (mirrors
    // CallRail's `bestCallDayOfWeek` definition at callrail.ts and the CTM
    // `transcription` / `transcriptionText` / `callSummary` / `agentInsights`
    // rows above). The `MetricDefinition.type` field's enum is
    // `"number" | "percentage" | "duration" | "currency"` — there is no
    // `"string"` literal. Downstream consumers MUST branch on `dataType`,
    // NOT `type`, when handling enum-valued metric rendering. Code Review
    // Rounds 1+2 flagged this as a potential mismatch; codebase pattern
    // confirmed and documented here.
    type: "number",
    dataType: "Enum",
    category: "Call Volume",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description:
      "Day of week (Mon-Sun) with the highest call volume in the date range. Mirrors CallRail 22.2's bestCallDayOfWeek.",
    synthetic: true,
  },

  // ============================================================================
  // Story 35.4 — Batch 1 Forms expansion (+8 metrics; bumps Forms group 4 → 12)
  // ============================================================================
  {
    key: "formCustomFieldsCount",
    label: "Form Custom Fields Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of form submissions that captured custom-field data (form.custom?.length > 0).",
    synthetic: true,
  },
  {
    key: "formCustomFieldsAvgPerSubmission",
    label: "Avg Custom Fields per Submission",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { numerator: "formCustomFieldsCount", denominator: "totalFormSubmissions" },
    category: "Form Submissions",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description:
      "Average number of custom-field entries per form submission. Surfaces intake-form richness for legal/dental verticals.",
    synthetic: true,
  },
  {
    key: "formAbandonmentCount",
    label: "Form Abandonment Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of forms started but not submitted (form.completed === false).",
    synthetic: true,
  },
  {
    key: "formAbandonmentRate",
    label: "Form Abandonment Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio",
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description:
      "formAbandonmentCount / totalFormSubmissions (records.length already includes both completed + abandoned attempts).",
    synthetic: true,
  },
  {
    key: "formDurationAvg",
    label: "Avg Form Fill Time",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalFormSubmissions" },
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "duration",
    description:
      "Average time (seconds) callers spent filling the form before submission.",
  },
  {
    key: "formReactorTriggersCount",
    label: "Form Reactor Triggers Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of forms attributed to a FormReactor template (form.form_id !== null).",
    synthetic: true,
  },
  {
    key: "formUniqueIdsCount",
    label: "Unique Form Templates Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description:
      "Count of distinct FormReactor template IDs that fired (distinct form.form_id).",
    synthetic: true,
  },
  {
    key: "formCallbackRequestedCount",
    label: "Callback Requested Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Form Submissions",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description:
      "Legal-vertical-specific: count of intake forms requesting a callback (form.custom.find(c => c.id === 'callback_requested')?.value === 'true').",
    synthetic: true,
  },

  // ============================================================================
  // Story 35.4 — Batch 2 SMS expansion (+4 metrics; bumps SMS group 5 → 9)
  // ============================================================================
  {
    key: "smsThreadDurationAvg",
    label: "Avg SMS Thread Duration",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "smsConversationsCount" },
    category: "SMS",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "<derived>",
    description:
      "Per-thread avg time (sec) between first inbound and last outbound SMS. Replaces Story 22.3 placeholder.",
    synthetic: true,
  },
  {
    key: "smsDeliveredCount",
    label: "SMS Delivered Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "SMS",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description: "Count of SMS messages with status = delivered.",
    synthetic: true,
  },
  {
    key: "smsFailedCount",
    label: "SMS Failed Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "SMS",
    compatibleWidgets: ["kpi"],
    apiField: "<derived>",
    description: "Count of SMS messages with status = failed.",
    synthetic: true,
  },
  {
    key: "smsDeliveryRate",
    label: "SMS Delivery Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "smsDeliveredCount", denominator: "outboundSmsMessages" },
    category: "SMS",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "<derived>",
    description: "smsDeliveredCount / totalSmsMessages.",
    synthetic: true,
  },

  // ============================================================================
  // Story 35.4 — Batch 3 AI scoring / Voice AI expansion (+4 metrics;
  // bumps CI group 4 → 8). All CI-gated.
  // ============================================================================
  {
    key: "spottedKeywordsCount",
    label: "Spotted Keywords Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversation Intelligence",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of calls with at least one spotted keyword (record.spotted?.length > 0). CI-gated.",
    synthetic: true,
  },
  {
    key: "spottedKeywordsTopList",
    label: "Top Spotted Keywords",
    type: "number",
    dataType: "Array",
    category: "Conversation Intelligence",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "<derived>",
    description:
      "Top-10 spotted keywords by frequency across all calls in the date range. Array of {keyword, count}. CI-gated.",
    synthetic: true,
  },
  {
    key: "agentInsightsTrueCount",
    label: "Agent Insights True Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversation Intelligence",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of calls where the AI fired an insight flag (record.agent_insights === true). CI-gated.",
    synthetic: true,
  },
  {
    key: "scorecardPassedCount",
    label: "Scorecards Passed Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversation Intelligence",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of calls where at least one Scorecard passed (record.scorecards?.some(s => s.passed === true)). Scorecards-gated (subscription add-on).",
    synthetic: true,
  },

  // ============================================================================
  // Story 35.4 — Batch 4 LeadReactors + Integrations expansion (+2 metrics;
  // NEW category headers). LeadReactors-gated + always-available.
  // ============================================================================
  {
    key: "leadReactorsConfiguredCount",
    label: "LeadReactors Configured",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "LeadReactors",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of active LeadReactor automation rules in the account (NOT date-range-bound — reflects current configuration). LeadReactors-gated.",
    synthetic: true,
  },
  {
    key: "integrationsConnectedCount",
    label: "Integrations Connected",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Integrations",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: "<derived>",
    description:
      "Count of currently-connected 3rd-party integrations on the CTM account (always available — base-plan customers see their connected partners too).",
    synthetic: true,
  },
];

// ============================================================================
// CTM_DIMENSIONS — 25 priority dimensions
// ============================================================================

/**
 * **Story 22.3 ships 25 of 40 catalogued CTM dimensions** — the
 * priority subset across Time + Source + UTM + Click IDs + Customer Geo
 * + Channel + Tracker + Quality + Tag.
 *
 * The remaining 15 dimensions (caller phone number / name / email
 * deliberately NOT exposed as dimensions in Story 22.3 for PII reasons,
 * UTM ad-group / match-type / ad-content / ad-network / ad-format,
 * GCLSRC, caller street / postal code / latitude / longitude, inbound
 * rate center, webvisit object, form custom fields) are tagged `gap`
 * for Stories 22.4 / 22.5+.
 */
export const CTM_DIMENSIONS: ReadonlyArray<DimensionDefinition> = [
  // Time (3)
  { key: "date", label: "Date", type: "Time" },
  { key: "hourOfDay", label: "Hour of Day", type: "Time" },
  { key: "dayOfWeek", label: "Day of Week", type: "Time" },
  // Tracking Source & Attribution (8)
  { key: "source", label: "Source", type: "Traffic" },
  { key: "sourceId", label: "Source ID", type: "Traffic" },
  { key: "trackingNumber", label: "Tracking Number", type: "Traffic" },
  { key: "trackingLabel", label: "Tracking Label", type: "Traffic" },
  { key: "businessNumber", label: "Business Number", type: "Traffic" },
  { key: "sourceTag", label: "Source Tag", type: "Traffic" },
  { key: "direction", label: "Direction", type: "Classification", exampleValues: ["inbound", "outbound"] },
  { key: "activityType", label: "Activity Type", type: "Classification", exampleValues: ["call", "sms", "chat", "form"] },
  // UTM Marketing (5)
  { key: "campaign", label: "Campaign", type: "Traffic" },
  { key: "utmSource", label: "UTM Source", type: "Traffic" },
  { key: "utmMedium", label: "UTM Medium", type: "Traffic" },
  { key: "utmKeyword", label: "UTM Keyword", type: "Search" },
  { key: "searchQuery", label: "Search Query", type: "Search" },
  // Click IDs (2)
  { key: "gclid", label: "Google Click ID", type: "Traffic" },
  { key: "gwbraid", label: "Google Web Branded Ad ID", type: "Traffic" },
  // Customer Geo (3 — PII-flagged at the sanitizer)
  { key: "customerCity", label: "Customer City", type: "Geo" },
  { key: "customerState", label: "Customer State", type: "Geo" },
  { key: "customerCountry", label: "Customer Country", type: "Geo" },
  // Quality (3)
  { key: "dialStatus", label: "Dial Status", type: "Classification", exampleValues: ["completed", "no-answer", "busy", "failed", "voicemail"] },
  { key: "saleConversionStatus", label: "Sale Conversion Status", type: "Conversion" },
  { key: "likelihoodDim", label: "Likelihood Filter", type: "Classification" },
  // Tag (1)
  { key: "tagList", label: "Tag List", type: "Classification" },
  // ── Story 35.4 — Batch 1 Forms dimensions (+3) ────────────────────
  { key: "formId", label: "Form ID", type: "Content" },
  { key: "formName", label: "Form Name", type: "Content" },
  {
    key: "formCompletedStatus",
    label: "Form Completed Status",
    type: "Classification",
    exampleValues: ["completed", "abandoned"],
  },
  // ── Story 35.4 — Batch 2 SMS dimensions (+2) ──────────────────────
  {
    key: "smsStatus",
    label: "SMS Status",
    type: "Classification",
    exampleValues: ["delivered", "failed", "queued", "sent"],
  },
  { key: "smsThreadId", label: "SMS Thread ID", type: "Channel" },
  // ── Story 35.4 — Batch 3 AI / CI dimensions (+2 — CI-gated) ───────
  { key: "spottedKeyword", label: "Spotted Keyword", type: "Search" },
  {
    key: "agentInsightsFlag",
    label: "Agent Insights Flag",
    type: "Classification",
    exampleValues: ["flagged", "clean"],
  },
  // ── Story 35.4 — Batch 4 Integration dimension (+1) ───────────────
  { key: "integrationPartner", label: "Integration Partner", type: "Classification" },
];

// ============================================================================
// Metadata reader
// ============================================================================

/**
 * Per-integration metadata read from the KV side-channel at
 * `ctm:meta:{integrationId}`. Populated at connect time by the route
 * handler (per AC #2 + AC #3).
 */
export interface CtmMetadata {
  readonly allowedAccountIds: ReadonlyArray<string>;
  readonly conversationIntelligenceEntitled: boolean;
  readonly region: CtmRegion;
  readonly keyScope?: "agency" | "account";
  readonly discoveredAt?: string;
  /**
   * Story 22.5 AC #14 — whether the customer's CTM account has call
   * recording enabled (Settings > Call Settings > Call Recording). Stamped
   * by `probeCtmRecordingsEnabled` at connect time. In fixture mode the
   * `loadCtmFixture` helper force-stamps `true` regardless of the KV value
   * (Round 4 ADR-D entitlement-bypass pattern).
   *
   * Defensive default: `false`.
   */
  readonly recordingsEnabled?: boolean;
  /**
   * Story 35.4 AC #7 — whether the customer's CTM plan includes
   * LeadReactors. Probed at connect time AND re-probed every 24h via the
   * cron health-check handler. In fixture mode `loadCtmFixture` force-
   * stamps `true`.
   *
   * Defensive default: `false`.
   */
  readonly leadReactorsEnabled?: boolean;
  /**
   * Story 35.4 AC #7 — whether the customer's CTM plan includes the
   * Scorecards Conversation Intelligence add-on. Probed at connect time
   * AND re-probed every 24h via the cron health-check handler. In
   * fixture mode `loadCtmFixture` force-stamps `true`.
   *
   * Defensive default: `false`.
   */
  readonly scorecardsEnabled?: boolean;
  /**
   * Story 35.4 AC #7 — last time the CI entitlement was re-probed (ISO
   * string). Used by the cron handler to throttle re-probes to once per
   * 24 hours per integration.
   */
  readonly ciProbeLastAt?: string;
  /**
   * Story 35.4 AC #7 — last time the LeadReactors entitlement was
   * re-probed (ISO string). Throttled to once per 24 hours.
   */
  readonly leadReactorsProbeLastAt?: string;
  /**
   * Story 35.4 AC #7 — last time the Scorecards entitlement was
   * re-probed (ISO string). Throttled to once per 24 hours.
   */
  readonly scorecardsProbeLastAt?: string;
}

/**
 * Public read accessor for the CTM integration's recording-enabled flag
 * (Story 22.5 AC #14 / Task 5 issue route).
 */
export async function readCtmRecordingsEnabled(
  env: { KV?: { get(key: string): Promise<string | null> } },
  integrationId: string,
): Promise<boolean> {
  const kv = env?.KV;
  if (!kv) return false;
  try {
    const raw = await kv.get(`ctm:meta:${integrationId}`);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { recordingsEnabled?: unknown };
    return parsed.recordingsEnabled === true;
  } catch {
    return false;
  }
}

/**
 * Public read accessor for the CTM region + accountId — needed by the
 * stream route to build the upstream URL (Story 22.5 AC #4).
 */
export async function readCtmRouteMetadata(
  env: { KV?: { get(key: string): Promise<string | null> } },
  integrationId: string,
): Promise<{ region: CtmRegion; firstAccountId: string | null }> {
  const fallback = {
    region: CTM_DEFAULT_REGION as CtmRegion,
    firstAccountId: null,
  };
  const kv = env?.KV;
  if (!kv) return fallback;
  try {
    const raw = await kv.get(`ctm:meta:${integrationId}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as {
      region?: unknown;
      allowedAccountIds?: unknown;
    };
    const region = isCtmRegion(parsed.region) ? parsed.region : CTM_DEFAULT_REGION;
    const ids = Array.isArray(parsed.allowedAccountIds)
      ? (parsed.allowedAccountIds as unknown[])
      : [];
    const firstAccountId =
      typeof ids[0] === "string" && (ids[0] as string).length > 0
        ? (ids[0] as string)
        : null;
    return { region, firstAccountId };
  } catch {
    return fallback;
  }
}

/**
 * Read CTM-specific metadata from the integration's KV side-channel.
 *
 * Defensive: returns sensible defaults when the side-channel is missing
 * (first-fetch race condition, or test contexts without KV). The
 * `allowedAccountIds` defaults to `[]` so the helper cross-check trips
 * the strict guard if the side-channel is wiped (matches CallRail 22.1
 * Pass 1 review fix).
 */
async function readCtmMetadata(ctx: AdapterContext): Promise<CtmMetadata> {
  const empty: CtmMetadata = {
    allowedAccountIds: [],
    conversationIntelligenceEntitled: false,
    region: CTM_DEFAULT_REGION,
  };
  const kv = (ctx.env as { KV?: { get(key: string): Promise<string | null> } })
    ?.KV;
  if (!kv) return empty;
  try {
    const key = `ctm:meta:${ctx.integrationId}`;
    const raw = await kv.get(key);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<CtmMetadata>;
    return {
      allowedAccountIds: Array.isArray(parsed.allowedAccountIds)
        ? (parsed.allowedAccountIds as ReadonlyArray<string>)
        : [],
      conversationIntelligenceEntitled: Boolean(
        parsed.conversationIntelligenceEntitled,
      ),
      region: isCtmRegion(parsed.region) ? parsed.region : CTM_DEFAULT_REGION,
      keyScope:
        parsed.keyScope === "agency" || parsed.keyScope === "account"
          ? parsed.keyScope
          : undefined,
      discoveredAt:
        typeof parsed.discoveredAt === "string"
          ? parsed.discoveredAt
          : undefined,
      // Story 35.4 — new entitlement flags + probe timestamps. Defensive
      // Boolean coercion: missing keys default to `false` (treat as not
      // entitled — the strictest gate).
      leadReactorsEnabled: parsed.leadReactorsEnabled === true,
      scorecardsEnabled: parsed.scorecardsEnabled === true,
      ciProbeLastAt:
        typeof parsed.ciProbeLastAt === "string"
          ? parsed.ciProbeLastAt
          : undefined,
      leadReactorsProbeLastAt:
        typeof parsed.leadReactorsProbeLastAt === "string"
          ? parsed.leadReactorsProbeLastAt
          : undefined,
      scorecardsProbeLastAt:
        typeof parsed.scorecardsProbeLastAt === "string"
          ? parsed.scorecardsProbeLastAt
          : undefined,
    };
  } catch {
    return empty;
  }
}

function emitStructuredLog(
  event: string,
  payload: Record<string, unknown>,
): void {
  console.log(
    JSON.stringify({
      event,
      ...payload,
      timestamp: new Date().toISOString(),
    }),
  );
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// ============================================================================
// Credential extraction
// ============================================================================

/**
 * Extract `(accessKey, secretKey)` from the OAuthCredentials shape.
 *
 * **Column-overload pattern (CTM-specific):** the credentials object
 * carries `accessToken = ACCESS_KEY` and `refreshToken = SECRET_KEY`
 * — both required for HTTP Basic auth. The decrypted values are
 * provided by the queue dispatcher per the OAuth-Integration
 * directive's standard credential-passing contract.
 *
 * Throws if either secret is missing (per AC #5 + #7 — `token_invalid`
 * routes to the `mark_revoked_notify_user` cron action so the user
 * gets a notification to rotate keys).
 */
function extractCredentials(credentials: OAuthCredentials): {
  accessKey: string;
  secretKey: string;
} {
  const accessKey = credentials.accessToken;
  const secretKey = credentials.refreshToken;
  if (typeof accessKey !== "string" || accessKey.length === 0) {
    throw new TypeError(
      "CTM credentials missing accessKey (oauth_tokens.access_token_encrypted is empty).",
    );
  }
  if (typeof secretKey !== "string" || secretKey.length === 0) {
    throw new TypeError(
      "CTM credentials missing secretKey (oauth_tokens.refresh_token_encrypted is empty — note the column-overload pattern stores SECRET_KEY here for CTM).",
    );
  }
  return { accessKey, secretKey };
}

// ============================================================================
// Aggregation helpers
// ============================================================================

/**
 * Compute the 8 derived Call Volume metrics from a list of `/calls.json`
 * records. Pure function — safe to call repeatedly.
 */
function aggregateCallVolume(
  records: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let answered = 0;
  let missed = 0;
  let busy = 0;
  let failed = 0;
  let voicemail = 0;
  let newCallers = 0;
  let excluded = 0;
  let redacted = 0;
  let tagged = 0;
  let talkTimeSum = 0;
  let talkTimeCount = 0;

  for (const r of records) {
    const status = String(r["dialStatus"] ?? "");
    if (status === "completed") answered++;
    else if (status === "no-answer") missed++;
    else if (status === "busy") busy++;
    else if (status === "failed") failed++;
    else if (status === "voicemail") voicemail++;
    if (r["isNewCaller"] === true) newCallers++;
    if (r["excluded"] === true) excluded++;
    if (r["redacted"] === true) redacted++;
    const tagList = r["tagList"];
    if (Array.isArray(tagList) && tagList.length > 0) tagged++;
    const tt = r["talkTime"];
    if (typeof tt === "number" && Number.isFinite(tt) && tt > 0) {
      talkTimeSum += tt;
      talkTimeCount++;
    }
  }

  const total = records.length;
  const safePercent = (numerator: number, denominator: number): number =>
    denominator > 0 ? numerator / denominator : 0;

  return {
    totalCalls: total,
    answeredCalls: answered,
    missedCalls: missed,
    busyCalls: busy,
    failedCalls: failed,
    voicemailCalls: voicemail,
    newCallerCount: newCallers,
    excludedCalls: excluded,
    redactedCallsCount: redacted,
    taggedCallsCount: tagged,
    answerRate: safePercent(answered, total),
    missRate: safePercent(missed, total),
    voicemailRate: safePercent(voicemail, total),
    newCallerRate: safePercent(newCallers, total),
    averageTalkTime:
      talkTimeCount > 0 ? talkTimeSum / talkTimeCount : 0,
  };
}

/**
 * Compute the 6 inline-sale metrics from a list of `/calls.json`
 * records. Reads `record.sale` opportunistically when present.
 */
function aggregateInlineSale(
  records: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let conversions = 0;
  let revenueSum = 0;
  let revenueCount = 0;
  let scoreSum = 0;
  let scoreCount = 0;
  const reps = new Set<string>();

  for (const r of records) {
    const sale = r["sale"];
    if (sale === null || sale === undefined || typeof sale !== "object") {
      continue;
    }
    const saleObj = sale as Record<string, unknown>;
    if (saleObj["conversion"] === true) conversions++;
    const value = saleObj["value"];
    if (typeof value === "number" && Number.isFinite(value)) {
      revenueSum += value;
      revenueCount++;
    }
    const score = saleObj["score"];
    if (typeof score === "number" && Number.isFinite(score)) {
      scoreSum += score;
      scoreCount++;
    }
    const rep = saleObj["serviceRepName"] ?? saleObj["service_rep_name"];
    if (typeof rep === "string" && rep.length > 0) {
      reps.add(rep);
    }
  }

  const total = records.length;
  return {
    conversionsCount: conversions,
    totalRevenue: revenueSum,
    averageSaleValue: revenueCount > 0 ? revenueSum / revenueCount : 0,
    averageSaleScore: scoreCount > 0 ? scoreSum / scoreCount : 0,
    conversionRate: total > 0 ? conversions / total : 0,
    serviceRepCount: reps.size,
  };
}

/**
 * Compute the SMS-derived metrics from a list of `/sms.json` records.
 */
function aggregateSms(
  records: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let inbound = 0;
  let outbound = 0;
  const conversations = new Set<string>();

  for (const r of records) {
    const direction = String(r["direction"] ?? "");
    if (direction === "inbound") inbound++;
    else if (direction === "outbound") outbound++;
    const callerNumber = r["callerNumber"];
    if (typeof callerNumber === "string" && callerNumber.length > 0) {
      conversations.add(callerNumber);
    }
  }

  return {
    totalSmsMessages: records.length,
    inboundSmsMessages: inbound,
    outboundSmsMessages: outbound,
    smsConversationsCount: conversations.size,
    // Average SMS response time computation requires a per-conversation
    // pass — left as 0 for Story 22.3 because the catalogue line 146
    // notes this is "per-conversation avg" which would require sorting
    // by conversation + timestamp. Story 22.4 ships the full
    // implementation once the SMS conversation threading helper lands.
    averageSmsResponseTime: 0,
  };
}

/**
 * Compute Form Submission derived metrics. Story 35.4 extends with
 * 8 NEW Batch 1 metrics: form custom-field richness, abandonment, fill
 * duration, FormReactor template counts, callback-requested count.
 *
 * The new metrics read from EITHER the top-level transformer-emitted
 * fields (e.g., `formCustomFields` / `formCompleted` / `formDuration` /
 * `formId`) OR a nested `form` object (if CTM's response embeds the
 * form-details under `form.*` per the catalogue line 304). Both shapes
 * are checked defensively — undocumented schema-drift surfaces as 0
 * counts rather than crashes.
 */
function aggregateFormSubmissions(
  records: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let formReactor = 0;
  const seenEmails = new Set<string>();
  let firstTimeSubmitters = 0;
  // Story 35.4 — Batch 1 expansion counters
  let customFieldsCount = 0;
  let customFieldsTotalEntries = 0;
  let abandonmentCount = 0;
  let durationSum = 0;
  let durationCount = 0;
  let formReactorTriggers = 0;
  const formIds = new Set<string>();
  let callbackRequested = 0;

  for (const r of records) {
    if (r["triggeredBy"] === "form_reactor") formReactor++;
    const email = r["email"];
    if (typeof email === "string" && email.length > 0) {
      if (!seenEmails.has(email)) {
        seenEmails.add(email);
        firstTimeSubmitters++;
      }
    }

    // Story 35.4 — read EITHER top-level transformer fields OR nested
    // form object. The transformer emits both shapes defensively.
    const formObj =
      r["form"] && typeof r["form"] === "object"
        ? (r["form"] as Record<string, unknown>)
        : null;
    const formCustom =
      (r["formCustomFields"] as unknown) ??
      (formObj ? formObj["custom"] : undefined);
    if (Array.isArray(formCustom) && formCustom.length > 0) {
      customFieldsCount++;
      customFieldsTotalEntries += formCustom.length;
      // Story 35.4 — Batch 1: legal-vertical callback_requested counter.
      // PII-redacted by the upstream sanitizer; value === "[redacted]"
      // here. We can only count via the `id` field being present, NOT
      // via its `value` (post-sanitization). Round 5 Edge Case Hunter
      // invariant — the metric is counted from the UNREDACTED record
      // path inside fetchMetrics BEFORE PII sanitization runs at the
      // log boundary, so the value comparison is sound on the
      // adapter-internal side.
      for (const f of formCustom as Array<Record<string, unknown>>) {
        if (
          typeof f === "object" &&
          f !== null &&
          f["id"] === "callback_requested" &&
          // Accept either boolean true OR string "true" (intake forms
          // sometimes serialize boolean values as strings).
          (f["value"] === true || f["value"] === "true")
        ) {
          callbackRequested++;
        }
      }
    }

    const completed =
      (r["formCompleted"] as unknown) ??
      (formObj ? formObj["completed"] : undefined);
    if (completed === false) abandonmentCount++;

    const duration =
      (r["formDuration"] as unknown) ??
      (formObj ? formObj["duration"] : undefined);
    if (typeof duration === "number" && Number.isFinite(duration) && duration > 0) {
      durationSum += duration;
      durationCount++;
    }

    const formId =
      (r["formId"] as unknown) ?? (formObj ? formObj["form_id"] : undefined);
    // Round 1 fix — accept BOTH string and number form_id values. CTM's
    // /forms.json sometimes returns numeric template IDs (esp. for
    // FormReactor templates created via the legacy dashboard).
    const formIdKey =
      typeof formId === "string" && formId.length > 0
        ? formId
        : typeof formId === "number" && Number.isFinite(formId)
          ? String(formId)
          : null;
    if (formIdKey !== null) {
      formIds.add(formIdKey);
      // **Round 2 fix:** only increment when the record was actually
      // attributed to FormReactor (triggered_by === "form_reactor").
      // Previously this counter mirrored totalFormSubmissions for any
      // form with a form_id — making it a duplicate metric instead of
      // FormReactor-attribution-specific.
      const triggeredBy =
        (r["triggeredBy"] as unknown) ??
        (formObj ? formObj["triggered_by"] : undefined);
      if (triggeredBy === "form_reactor") {
        formReactorTriggers++;
      }
    }
  }

  const total = records.length;
  return {
    totalFormSubmissions: total,
    formReactorConversions: formReactor,
    firstFormSubmissionsCount: firstTimeSubmitters,
    // Story 35.4 — Batch 1 NEW metrics
    formCustomFieldsCount: customFieldsCount,
    formCustomFieldsAvgPerSubmission:
      total > 0 ? customFieldsTotalEntries / total : 0,
    formAbandonmentCount: abandonmentCount,
    // **Round 2 fix:** denominator is `total` (records.length) — which
    // already includes BOTH completed AND abandoned attempts (every
    // record returned by /forms.json is an attempted submission).
    // Previous shape `abandonmentCount / (total + abandonmentCount)`
    // double-counted abandoned forms and asymptotically capped at 50%.
    formAbandonmentRate: total > 0 ? abandonmentCount / total : 0,
    formDurationAvg: durationCount > 0 ? durationSum / durationCount : 0,
    formReactorTriggersCount: formReactorTriggers,
    formUniqueIdsCount: formIds.size,
    formCallbackRequestedCount: callbackRequested,
  };
}

/**
 * Story 35.4 — Batch 2 SMS expansion aggregator. Computes:
 *  - `smsThreadDurationAvg` — per-thread avg time between first inbound
 *    and last outbound SMS (replaces Story 22.3 placeholder which was 0).
 *  - `smsDeliveredCount` — count of status === "delivered".
 *  - `smsFailedCount` — count of status === "failed".
 *  - `smsDeliveryRate` — delivered / total.
 */
function aggregateSmsExpansion(
  records: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let delivered = 0;
  let failed = 0;
  // Group records by threadId for the duration computation. A "thread"
  // is identified by `threadId` (preferred) or falls back to
  // `callerNumber` (Story 22.3 synthetic fallback shape).
  const threads = new Map<string, Array<Record<string, unknown>>>();

  for (const r of records) {
    const status = r["smsStatus"] ?? r["status"];
    if (status === "delivered") delivered++;
    else if (status === "failed") failed++;

    const threadKey =
      (typeof r["threadId"] === "string" && (r["threadId"] as string).length > 0
        ? (r["threadId"] as string)
        : null) ??
      (typeof r["callerNumber"] === "string" && (r["callerNumber"] as string).length > 0
        ? (r["callerNumber"] as string)
        : null);
    if (threadKey === null) continue;
    const bucket = threads.get(threadKey) ?? [];
    bucket.push(r);
    threads.set(threadKey, bucket);
  }

  // Per-thread avg duration: for each thread, find the FIRST inbound
  // timestamp and the LAST outbound timestamp; the diff (in seconds) is
  // that thread's "duration". Average across threads with both an
  // inbound and an outbound. Threads with only inbound OR only outbound
  // contribute 0. Round 5 Edge Case 5 invariant — timestamps are
  // already UTC-normalized via the transformer.
  let threadDurationSum = 0;
  let threadDurationCount = 0;
  for (const [, msgs] of threads) {
    let firstInbound: number | null = null;
    let lastOutbound: number | null = null;
    for (const m of msgs) {
      const direction = String(m["direction"] ?? "");
      const timestamp = pickSmsTimestampMs(m);
      if (timestamp === null) continue;
      if (direction === "inbound") {
        if (firstInbound === null || timestamp < firstInbound) {
          firstInbound = timestamp;
        }
      } else if (direction === "outbound") {
        if (lastOutbound === null || timestamp > lastOutbound) {
          lastOutbound = timestamp;
        }
      }
    }
    if (
      firstInbound !== null &&
      lastOutbound !== null &&
      lastOutbound >= firstInbound
    ) {
      threadDurationSum += (lastOutbound - firstInbound) / 1000;
      threadDurationCount++;
    }
  }

  // **Round 2 fix:** denominator is terminal-state messages
  // (delivered + failed), NOT records.length. Queued/sent/in-flight
  // messages haven't reached a delivery decision yet, so including
  // them in the denominator dilutes the rate during high-throughput
  // outbound windows. Spec-compliant industry shape mirrors what
  // Twilio's SMS analytics use.
  const terminalCount = delivered + failed;
  return {
    smsDeliveredCount: delivered,
    smsFailedCount: failed,
    smsDeliveryRate: terminalCount > 0 ? delivered / terminalCount : 0,
    smsThreadDurationAvg:
      threadDurationCount > 0
        ? threadDurationSum / threadDurationCount
        : 0,
  };
}

/**
 * Helper: extract the best timestamp (in epoch-ms) from an SMS record.
 * Prefers `sentTimeUtc` / `receivedTimeUtc` (Story 35.4 — Batch 2) and
 * falls back to `createdAtUtc` (Story 22.3 baseline). Returns null on
 * un-parseable or missing timestamps.
 *
 * **Round 1 fix:** also accepts numeric epoch timestamps (seconds OR
 * milliseconds) — CTM's webhook payloads sometimes serialize unix
 * timestamps as numbers (cf. `unix_time` on calls). Mirrors the
 * `normalizeCtmTimestampToUtc` numeric path.
 */
function pickSmsTimestampMs(record: Record<string, unknown>): number | null {
  const candidates = [
    record["sentTimeUtc"],
    record["receivedTimeUtc"],
    record["createdAtUtc"],
    record["sentTime"],
    record["receivedTime"],
    record["createdAt"],
    record["unixTime"],
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.length > 0) {
      const ms = Date.parse(c);
      if (Number.isFinite(ms)) return ms;
    }
    if (typeof c === "number" && Number.isFinite(c)) {
      // Heuristic: values < 1e12 are seconds-since-epoch, otherwise ms.
      return c < 1e12 ? c * 1000 : c;
    }
  }
  return null;
}

/**
 * Story 35.4 — Batch 3 AI scoring / Voice AI expansion aggregator.
 * Computes:
 *  - `spottedKeywordsCount` — calls with spotted?.length > 0
 *  - `spottedKeywordsTopList` — top-10 spotted keywords by frequency
 *  - `agentInsightsTrueCount` — calls with agent_insights === true
 *  - `scorecardPassedCount` — calls with at least one scorecard.passed
 */
function aggregateAiExpansion(
  records: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let spottedCalls = 0;
  let agentInsightsTrue = 0;
  let scorecardPassed = 0;
  const keywordFreq = new Map<string, number>();

  for (const r of records) {
    const spotted = r["spotted"];
    if (Array.isArray(spotted) && spotted.length > 0) {
      spottedCalls++;
      // Round 1 Edge Case Hunter — dedupe per-call so "consultation"
      // mentioned 3× in one call counts once in keyword frequency. The
      // `spottedKeywordsCount` metric is call-frequency (above);
      // `spottedKeywordsTopList` is now also call-frequency for shape
      // consistency.
      // **Round 2 fix:** trim + skip whitespace-only entries. CI returns
      // whitespace/non-breaking-space tokens on noisy transcripts that
      // would otherwise pollute the top-10 list with `{keyword: " ",
      // count: 12}`-shaped garbage.
      const uniqueInCall = new Set<string>();
      for (const k of spotted) {
        if (typeof k === "string") {
          const trimmed = k.trim();
          if (trimmed.length > 0) uniqueInCall.add(trimmed);
        }
      }
      for (const k of uniqueInCall) {
        keywordFreq.set(k, (keywordFreq.get(k) ?? 0) + 1);
      }
    }
    if (r["agentInsights"] === true) agentInsightsTrue++;
    const scorecards = r["scorecards"];
    if (Array.isArray(scorecards)) {
      for (const s of scorecards) {
        if (
          typeof s === "object" &&
          s !== null &&
          (s as Record<string, unknown>)["passed"] === true
        ) {
          scorecardPassed++;
          break; // only count the CALL once even if multiple scorecards pass
        }
      }
    }
  }

  // Top-10 keywords by frequency, descending. Round 1 Edge Case Hunter —
  // secondary sort by alphabetic keyword to break ties deterministically
  // (otherwise V8's non-stable sort can produce different top-10s for
  // tied keywords across runs / cache invalidations).
  const topKeywords = [...keywordFreq.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 10)
    .map(([keyword, count]) => ({ keyword, count }));

  return {
    spottedKeywordsCount: spottedCalls,
    spottedKeywordsTopList: topKeywords,
    agentInsightsTrueCount: agentInsightsTrue,
    scorecardPassedCount: scorecardPassed,
  };
}

/**
 * Story 35.4 — Batch 4 LeadReactors aggregator. Computes:
 *  - `leadReactorsConfiguredCount` — total active LeadReactor rules.
 *
 * Pulls from the `/triggers.json` records (NOT date-range bound —
 * reflects current configuration state).
 */
function aggregateLeadReactors(
  triggers: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  // **Round 2 fix — TRULY strict positive check.** Count ONLY records
  // where the disabled signal is absent AND a positive signal is present.
  // Previously, fully-sparse records (no `enabled`, no `status`) were
  // counted — that contradicted the Round 1 "strict mode" intent and
  // silently inflated counts.
  //
  // Disabled signals (any one → skip): `enabled === false` OR status in
  //   archived/draft/deleted/disabled/inactive.
  // Enabled signals (any one → count): `enabled === true` OR status ===
  //   active/enabled.
  // Records matching neither → uncertain → SKIP (defensive — don't
  // overstate).
  let configured = 0;
  for (const t of triggers) {
    const enabledFlag = t["enabled"];
    const status = t["status"];
    const isExplicitlyDisabled =
      enabledFlag === false ||
      status === "archived" ||
      status === "draft" ||
      status === "deleted" ||
      status === "disabled" ||
      status === "inactive";
    if (isExplicitlyDisabled) continue;
    const isExplicitlyEnabled =
      enabledFlag === true || status === "active" || status === "enabled";
    if (isExplicitlyEnabled) configured++;
  }
  return {
    leadReactorsConfiguredCount: configured,
  };
}

/**
 * Story 35.4 — Batch 4 Integrations aggregator. Computes:
 *  - `integrationsConnectedCount` — count of partners with status === "connected".
 */
function aggregateIntegrations(
  integrations: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  let connected = 0;
  for (const i of integrations) {
    if (i["status"] === "connected") connected++;
  }
  return {
    integrationsConnectedCount: connected,
  };
}

// ============================================================================
// Fixture-mode helper (Story 22.4 — closes Epic 22 CTM slice)
// ============================================================================

/**
 * Load CTM demo fixture for the calling client. Mirrors the CallRail 22.2
 * `loadCallRailFixture` helper at `packages/integrations/src/callrail/
 * callrail.ts:1004` with 5 deliberate divergences:
 *
 *  1. **NO retention pre-flight to bypass** (DIVERGENT from CallRail 22.2).
 *     CTM has no documented data-retention ceiling per
 *     `www.calltrackingmetrics.com/legal/data-protection-addendum-2025/`
 *     verified 2026-05-15 — no equivalent to CallRail's 25-month
 *     `validateCallRailDateRangeAgainstRetention` skip. A future agent
 *     should NOT search for one here.
 *
 *  2. **`metadata.region = "us"` force-stamp** (Round 2 Pre-mortem C).
 *     Fixture mode MUST NEVER reference the EU hostname which is
 *     catalogue-claimed-but-runtime-probed per Story 22.3 AC #2. Even a
 *     metadata leak would risk a Story-22.6+ region-routing regression
 *     test misfiring.
 *
 *  3. **`metadata.keyScope = "account"` force-stamp** (Round 2 Pre-mortem
 *     B). Demo workspace has exactly 1 account; preventing the
 *     `keyScope: "agency"` UI from rendering empty-state.
 *
 *  4. **`metadata.conversationIntelligenceEntitled = true` force-stamp**
 *     (Round 4 ADR-D — mirrors CallRail 22.2 + Moz 20.8 entitlement-
 *     bypass pattern). Likelihood Quality Pie widget demos with populated
 *     state regardless of any KV-stored metadata.
 *
 *  5. **NO `buildCtmBasicAuthHeader` invocation in this call path**
 *     (Round 3 Red Team Attack #3 dual-secret-leak invariant). The
 *     short-circuit runs BEFORE `extractCredentials` so the Base64
 *     `Authorization: Basic <...>` header is never constructed when
 *     `ctx.fixtures === true`. This is THE dual-secret-leak prevention
 *     gate for fixture mode.
 *
 * Same invariants as CallRail 22.2:
 *
 *  - **Cross-tenant fixture-data leak protection** (Round 3 Red Team
 *    Attack #1). The helper resolves `clientSlug` via
 *    `getClientSlug(db, ctx.tenantId, ctx.clientId)` — the SQL-layer
 *    `(tenantId, clientId)` scope enforces isolation. Never bypass the
 *    resolver.
 *
 *  - **No production network calls** (Round 4 QA invariant — fixture-
 *    mode-vs-production-mode separation): `safeFetchCtm` /
 *    `fetchAllCtmCalls` / `fetchAllCtmFormSubmissions` /
 *    `fetchAllCtmSmsMessages` / `discoverCtmAccounts` /
 *    `probeCtmConversationIntelligence` are NEVER called.
 *
 *  - **No production cache writes** (Round 3 Red Team Attack #2 —
 *    production-cache-write-prevention). The helper reads ONLY
 *    `fixture:calltrackingmetrics:{slug}` keys and NEVER writes to ANY
 *    KV key. Production cache prefixes (`ctm:*`, `ctm:meta:*`,
 *    `ctm:probe:*`) are not touched.
 *
 *  - **Structured-log telemetry per AC #19** — emits one of
 *    `ctm_demo_fixture_loaded` / `_missing` / `_binding_missing` /
 *    `_resolver_failed` per invocation. NEVER emits PII (caller_number
 *    / name / email / transcription / summary / SMS message) — only the
 *    aggregate `recordCount`.
 *
 *  - **p99 < 50ms** when the fixture is KV-resident.
 */
async function loadCtmFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
) {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    emitStructuredLog("ctm_demo_fixture_binding_missing", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      missingBinding: "kv",
    });
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load CTM demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    emitStructuredLog("ctm_demo_fixture_binding_missing", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      missingBinding: "db",
    });
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve CTM demo client slug",
      retryable: false,
    });
  }

  // Defensive validation that ctx.tenantId + ctx.clientId are non-empty
  // strings BEFORE calling getClientSlug. Round 3 Red Team Attack #1
  // cross-tenant leak protection.
  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    return adapterErr({
      code: "permission_denied",
      message:
        "CTM demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  // Validate date range — reversed ranges silently return zero records and
  // Invalid Date crashes via toISOString. NOTE: NO retention pre-flight
  // (DIVERGENT from CallRail 22.2 — see top-of-function JSDoc).
  const startTime = dateRange.start?.getTime?.();
  const endTime = dateRange.end?.getTime?.();
  if (
    typeof startTime !== "number" ||
    !Number.isFinite(startTime) ||
    typeof endTime !== "number" ||
    !Number.isFinite(endTime)
  ) {
    return adapterErr({
      code: "invalid_request",
      message:
        "CTM demo fixture: dateRange.start and dateRange.end must be finite Date instances",
      retryable: false,
    });
  }
  if (startTime > endTime) {
    return adapterErr({
      code: "invalid_request",
      message:
        "CTM demo fixture: dateRange.start must be on or before dateRange.end",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    // Round 3 Red Team Attack #1 — getClientSlug enforces the
    // (tenantId, clientId) scope at the SQL query layer. We MUST pass
    // both. Bypassing this resolver would silently leak fixture data
    // across tenants.
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    emitStructuredLog("ctm_demo_fixture_resolver_failed", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      reason: err instanceof Error ? err.message : String(err),
    });
    return adapterErr({
      code: "account_not_found",
      message: `CTM demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "calltrackingmetrics",
    clientSlug,
  );

  if (metrics === null) {
    emitStructuredLog("ctm_demo_fixture_missing", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      clientSlug,
      kvKey: `fixture:calltrackingmetrics:${clientSlug}`,
    });
    return adapterErr({
      code: "account_not_found",
      message: `CTM demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end,
  );

  const filteredWarnings = [...(filtered.warnings ?? [])];
  if (filtered.records.length === 0) {
    filteredWarnings.push({
      reportType: "*",
      reason: `CTM demo fixture filtered to zero records for date range ${dateRange.start.toISOString().split("T")[0]} → ${dateRange.end.toISOString().split("T")[0]}. Try a wider window or check the fixture upload.`,
    });
  }

  // Force-overwrite metadata per AC #8 (Round 2 Pre-mortem B+C, Round 4
  // ADR-D, Story 22.3 cache-TTL convention).
  //
  // dataFinalizedThrough: clamp to min(now - 1h, dateRange.end - 1h) so
  // the demo doesn't claim "data is from yesterday" when the user
  // narrowed the date range.
  const lagMs = CTM_FRESHNESS_LAG_HOURS * 60 * 60 * 1000;
  const finalizedFromNow = Date.now() - lagMs;
  const finalizedFromRange = dateRange.end.getTime() - lagMs;
  const SANE_FINALIZED_FLOOR_MS = Date.UTC(2020, 0, 1);
  const dataFinalizedThroughMs =
    finalizedFromRange >= SANE_FINALIZED_FLOOR_MS
      ? Math.min(finalizedFromNow, finalizedFromRange)
      : finalizedFromNow;
  const dataFinalizedThrough = new Date(dataFinalizedThroughMs).toISOString();

  const cachedMeta = filtered.metadata ?? {};
  if (Object.keys(cachedMeta).length === 0) {
    filteredWarnings.push({
      reportType: "*",
      reason:
        "CTM demo fixture has no metadata; re-run pnpm fixtures:generate + pnpm fixtures:upload to refresh.",
    });
  }

  // Round 2 Pre-mortem C — accountId fallback must coerce empty string to
  // the demo default; otherwise `allowedAccountIds: [""]` would silently
  // bypass downstream cross-tenant guards.
  const allowedAccountIdFallback =
    typeof ctx.accountId === "string" && ctx.accountId.length > 0
      ? ctx.accountId
      : "demo-810002";

  const result = {
    ...filtered,
    warnings: filteredWarnings,
    metadata: {
      ...cachedMeta,
      dataFreshnessLagHours: CTM_FRESHNESS_LAG_HOURS,
      dataFinalizedThrough,
      // Round 4 ADR-D — fixture-mode force-overwrite CI to true so the
      // Likelihood Quality Pie widget demos with populated state
      // regardless of any KV-stored value.
      conversationIntelligenceEntitled: true,
      // Story 22.5 AC #14 — fixture mode force-stamps recordings as enabled
      // so the demo workspace always renders the ▶ Play button. Mirrors the
      // CI entitlement force-stamp above (Round 4 ADR-D entitlement-bypass).
      recordingsEnabled: true,
      // Story 35.4 AC #12 / Task 10.2 — fixture mode force-stamps the
      // 2 new entitlement flags so Summit Legal demos always render the
      // LeadReactors panel + Scorecards-gated widgets in the "best-case"
      // entitlement state. Mirrors the CI entitlement force-stamp above
      // (Round 4 ADR-D entitlement-bypass pattern).
      leadReactorsEnabled: true,
      scorecardsEnabled: true,
      // Round 2 Pre-mortem C — fixture mode MUST NEVER reference EU.
      region: "us" as const,
      // Round 2 Pre-mortem B — demo workspace has exactly 1 account.
      keyScope: "account" as const,
      allowedAccountIds: [allowedAccountIdFallback],
    },
  };

  emitStructuredLog("ctm_demo_fixture_loaded", {
    tenantId: ctx.tenantId,
    clientId: ctx.clientId,
    integrationId: ctx.integrationId,
    clientSlug,
    recordCount: result.recordCount,
    durationMs: Date.now() - startedAt,
  });

  return adapterOk(result);
}

// ============================================================================
// Adapter
// ============================================================================

export const ctmAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  authType: "api-key",
  /**
   * Rate limits per AC #8: 60 req/min conservative ceiling (~5×
   * margin vs the ~300 req/min/account community-reported soft cap);
   * burst 1 prevents thundering-herd across queue consumers; 100K/day
   * is a generous ceiling sized for agency reseller use cases where a
   * single key spans hundreds of sub-accounts.
   */
  rateLimits: {
    requestsPerMinute: 60,
    burstLimit: 1,
    requestsPerDay: 100_000,
  },

  async validateConnection(ctx, credentials) {
    // Story 22.4 Code Review Round 2 — fixture-mode short-circuit. Same
    // rationale as the fetchMetrics + discoverEndpoints paths: in demo mode,
    // calling validateCtmConnection would hit the live CTM API (with REAL
    // credentials if any are cached for this client_id from a prior real
    // connection). Round 3 Red Team Attack #2 "production ctm:meta:* never
    // touched in fixture mode" invariant requires consistent gating across
    // ALL 4 adapter methods (validateConnection, discoverAccounts,
    // fetchMetrics, discoverEndpoints).
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixturesEnabled) {
      return adapterOk(true);
    }
    void ctx;
    try {
      const { accessKey, secretKey } = extractCredentials(credentials);
      const metadata = await readCtmMetadata(ctx);
      await validateCtmConnection(accessKey, secretKey, metadata.region);
      return adapterOk(true);
    } catch (err) {
      if (err instanceof CtmApiCallError) {
        return adapterErr({
          code:
            err.type === "unauthorized"
              ? "token_expired"
              : err.type === "forbidden"
              ? "permission_denied"
              : err.type === "rate_limit_exceeded"
              ? "rate_limited"
              : err.type === "provider_unavailable"
              ? "provider_unavailable"
              : "invalid_response",
          message: err.message,
          retryable: err.retryable,
          providerStatus: err.status,
        });
      }
      return adapterErr({
        code: "token_invalid",
        message:
          err instanceof Error ? err.message : "CTM credentials are invalid",
        retryable: false,
      });
    }
  },

  async discoverAccounts(ctx, credentials) {
    // Story 22.4 Code Review Round 2 — fixture-mode short-circuit. In demo
    // mode return a single-account stub matching the Summit Legal fixture
    // (demo-810002). Same Round 3 Red Team Attack #2 invariant carry-forward.
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixturesEnabled) {
      const stubAccount: AccountInfo = {
        id: ctx.accountId || "demo-810002",
        name: "Summit Legal Group (Demo CTM)",
        platform: PLATFORM as Platform,
        metadata: { status: "active" },
      };
      return adapterOk([stubAccount]);
    }
    try {
      const { accessKey, secretKey } = extractCredentials(credentials);
      const metadata = await readCtmMetadata(ctx);
      const result = await discoverCtmAccounts(
        accessKey,
        secretKey,
        metadata.region,
      );
      const accountInfos: AccountInfo[] = result.accounts.map(
        (a: CtmAccount) => ({
          id: a.id,
          name: a.name,
          platform: PLATFORM as Platform,
          metadata: a.status ? { status: a.status } : undefined,
        }),
      );
      return adapterOk(accountInfos);
    } catch (err) {
      if (err instanceof CtmApiCallError) {
        return adapterErr({
          code:
            err.type === "unauthorized"
              ? "token_expired"
              : err.type === "forbidden"
              ? "permission_denied"
              : "provider_unavailable",
          message: err.message,
          retryable: err.retryable,
          providerStatus: err.status,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message:
          err instanceof Error ? err.message : "CTM account discovery failed",
        retryable: true,
      });
    }
  },

  async fetchMetrics(ctx, credentials, dateRange) {
    // Story 22.4 — fixture-mode short-circuit (AC #8). MUST run BEFORE any
    // production-path read: extractCredentials (would throw on empty
    // dual-secret), readCtmMetadata (would read ctm:meta:* side-channel),
    // empty-allowlist guard, account-id check, Promise.allSettled fan-out.
    // Round 3 Red Team Attack #3 dual-secret-leak invariant: this gate
    // ensures `buildCtmBasicAuthHeader` is NEVER invoked when
    // `ctx.fixtures === true`. The helper itself is OUTSIDE the try-catch
    // because fixture-mode errors are well-defined and don't need the
    // production CTM-error mapping.
    //
    // Code Review Round 1 Edge Hunter #6 — strict `=== true` check
    // intentionally rejects truthy-non-true values (`"true"` string, `1`
    // number, truthy object). If a future migration moves `fixtures` to a
    // non-boolean column (e.g., SQLite `1`/`0` integer hydration), the
    // production path would silently run against demo credentials. Emit a
    // diagnostic log when a truthy-but-not-strictly-true value is seen so
    // the drift surfaces at runtime BEFORE it can leak credentials.
    const rawFixtures = (ctx as unknown as { fixtures?: unknown }).fixtures;
    if (rawFixtures !== undefined && rawFixtures !== true && rawFixtures !== false) {
      emitStructuredLog("ctm_fixtures_flag_unexpected_type", {
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        actualType: typeof rawFixtures,
        actualValue: String(rawFixtures).slice(0, 50),
      });
    }
    const fixturesEnabled = rawFixtures === true;
    if (fixturesEnabled) {
      return loadCtmFixture(ctx, dateRange);
    }

    try {
      const { accessKey, secretKey } = extractCredentials(credentials);
      const metadata = await readCtmMetadata(ctx);

      // Strict cross-tenant guard: refuse to fetch if the requested
      // account isn't in the allowlist. Carries forward the CallRail
      // 22.1 + Round 3 Red Team Attack #5 invariant.
      if (metadata.allowedAccountIds.length === 0) {
        return adapterErr({
          code: "account_not_found",
          message:
            "CTM integration metadata is missing or wiped — re-authorize the integration in the integrations dashboard.",
          retryable: false,
        });
      }
      const accountId = ctx.accountId;
      if (!accountId || !metadata.allowedAccountIds.includes(accountId)) {
        return adapterErr({
          code: "account_not_found",
          message: `CTM account ${accountId} is not in the discovered allowlist — re-authorize the integration to refresh the account list.`,
          retryable: false,
        });
      }

      const startDate = toIsoDate(dateRange.start);
      const endDate = toIsoDate(dateRange.end);

      const startedAt = Date.now();

      // Story 35.4 AC #11 — extend from 3-endpoint fan-out to 5-endpoint
      // conditional fan-out:
      //   - Always: calls, forms, sms (Story 22.3 — unchanged)
      //   - Conditional: triggers (only if metadata.leadReactorsEnabled === true)
      //   - Always: integrations (even base-plan returns empty — NOT an error)
      //
      // Promise.allSettled so any endpoint failure does NOT cascade.
      // CRITICAL — CI/LeadReactor/Scorecard field gating: the
      // computeCtmFields helper strips gated fields per the entitlement
      // flags. AC #11 says when CI is false the calls URL drops CI fields;
      // when scorecards is false the scorecards field drops.
      const leadReactorsEnabled = metadata.leadReactorsEnabled === true;
      const scorecardsEnabled = metadata.scorecardsEnabled === true;

      // Run all 5 endpoint fetches via Promise.allSettled with explicit
      // per-promise typing. The tuple-typed call preserves each promise's
      // distinct return shape — TypeScript widens to PromiseSettledResult<T>
      // per element, NOT to PromiseSettledResult<unknown>.
      const [
        callsResult,
        formsResult,
        smsResult,
        triggersResult,
        integrationsResult,
      ] = await Promise.allSettled([
        fetchAllCtmCalls({
          accessKey,
          secretKey,
          region: metadata.region,
          accountId,
          startDate,
          endDate,
          conversationIntelligenceEntitled:
            metadata.conversationIntelligenceEntitled,
          leadReactorsEnabled,
          scorecardsEnabled,
        }),
        fetchAllCtmFormSubmissions({
          accessKey,
          secretKey,
          region: metadata.region,
          accountId,
          startDate,
          endDate,
        }),
        fetchAllCtmSmsMessages({
          accessKey,
          secretKey,
          region: metadata.region,
          accountId,
          startDate,
          endDate,
        }),
        // Story 35.4 — conditional /triggers.json fetch. Even if the
        // metadata flag is false we surface a `null` promise that
        // resolves to `null` so the indexing stays stable; the
        // aggregator simply skips when records are empty.
        leadReactorsEnabled
          ? fetchAllCtmTriggers({
              accessKey,
              secretKey,
              region: metadata.region,
              accountId,
            })
          : Promise.resolve(null as Awaited<ReturnType<typeof fetchAllCtmTriggers>> | null),
        // Always: integrations endpoint. Base-plan returns empty array —
        // that's the expected response, NOT an error (Round 5 Edge Case 5).
        fetchAllCtmIntegrations({
          accessKey,
          secretKey,
          region: metadata.region,
          accountId,
        }),
      ] as const);

      const warnings: { reportType: string; reason: string }[] = [];
      const allRecords: Record<string, unknown>[] = [];
      let callRecords: ReadonlyArray<Record<string, unknown>> = [];
      let formRecords: ReadonlyArray<Record<string, unknown>> = [];
      let smsRecords: ReadonlyArray<Record<string, unknown>> = [];
      let triggerRecords: ReadonlyArray<Record<string, unknown>> = [];
      let integrationRecords: ReadonlyArray<Record<string, unknown>> = [];

      if (callsResult.status === "fulfilled") {
        callRecords = callsResult.value.records;
        allRecords.push(...callRecords);
        if (callsResult.value.truncated) {
          warnings.push({
            reportType: "calls-list",
            reason: `Pagination truncated at ${callsResult.value.pagesWalked} pages (~${callsResult.value.totalEntries} total entries). Chunk the date range to retrieve more.`,
          });
        }
      } else {
        const err = callsResult.reason;
        const message =
          err instanceof Error ? err.message : "CTM /calls.json failed";
        warnings.push({ reportType: "calls-list", reason: message });
        emitStructuredLog("ctm_calls_fetch_failed", {
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId,
          message,
        });
      }

      if (formsResult.status === "fulfilled") {
        formRecords = formsResult.value.records;
        allRecords.push(...formRecords);
        if (formsResult.value.truncated) {
          warnings.push({
            reportType: "form-submissions",
            reason: `Pagination truncated at ${formsResult.value.pagesWalked} pages.`,
          });
        }
      } else {
        const err = formsResult.reason;
        const message =
          err instanceof Error ? err.message : "CTM /forms.json failed";
        warnings.push({ reportType: "form-submissions", reason: message });
        emitStructuredLog("ctm_forms_fetch_failed", {
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId,
          message,
        });
      }

      if (smsResult.status === "fulfilled") {
        smsRecords = smsResult.value.records;
        allRecords.push(...smsRecords);
        if (smsResult.value.truncated) {
          warnings.push({
            reportType: "sms",
            reason: `Pagination truncated at ${smsResult.value.pagesWalked} pages.`,
          });
        }
      } else {
        const err = smsResult.reason;
        const message =
          err instanceof Error ? err.message : "CTM /sms.json failed";
        warnings.push({ reportType: "sms", reason: message });
        emitStructuredLog("ctm_sms_fetch_failed", {
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId,
          message,
        });
      }

      // Story 35.4 — Triggers result handling. When LeadReactors is not
      // enabled, the promise resolved to `null` (skipped); we surface
      // empty records. When the API returned `entitled: false` (403/422
      // graceful degradation), records is empty too — the route handler
      // can use this signal to flip `metadata.leadReactorsEnabled = false`
      // on subsequent runs.
      if (
        triggersResult.status === "fulfilled" &&
        triggersResult.value !== null
      ) {
        const v = triggersResult.value;
        triggerRecords = v.records;
        allRecords.push(...triggerRecords);
        if (v.truncated) {
          warnings.push({
            reportType: "triggers",
            reason: `Pagination truncated at ${v.pagesWalked} pages.`,
          });
        }
        if (v.entitled === false) {
          warnings.push({
            reportType: "triggers",
            reason: "CTM LeadReactors add-on not enabled on this account.",
          });
        }
      } else if (triggersResult.status === "rejected") {
        const err = triggersResult.reason;
        const message =
          err instanceof Error ? err.message : "CTM /triggers.json failed";
        warnings.push({ reportType: "triggers", reason: message });
        emitStructuredLog("ctm_triggers_fetch_failed", {
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId,
          message,
        });
      }

      // Story 35.4 — Integrations result handling. Always-fetched even
      // when base-plan; empty array is the expected base-plan response.
      if (integrationsResult.status === "fulfilled") {
        integrationRecords = integrationsResult.value.records;
        allRecords.push(...integrationRecords);
        if (integrationsResult.value.truncated) {
          warnings.push({
            reportType: "integrations",
            reason: `Pagination truncated at ${integrationsResult.value.pagesWalked} pages.`,
          });
        }
      } else {
        const err = integrationsResult.reason;
        const message =
          err instanceof Error ? err.message : "CTM /integrations.json failed";
        warnings.push({ reportType: "integrations", reason: message });
        emitStructuredLog("ctm_integrations_fetch_failed", {
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId,
          message,
        });
      }

      // Aggregate derived metrics. The aggregates are emitted as an
      // additional `derived-aggregates` record so downstream widget
      // renderers can pick either per-row records (for data-tables) or
      // the aggregate (for KPIs). Story 35.4 extends with Batch 1-4
      // expansion aggregators.
      const aggregate = {
        _reportType: "derived-aggregates",
        accountId,
        ...aggregateCallVolume(callRecords),
        ...aggregateInlineSale(callRecords),
        ...aggregateFormSubmissions(formRecords),
        ...aggregateSms(smsRecords),
        // Story 35.4 — Batch 2 SMS expansion
        ...aggregateSmsExpansion(smsRecords),
        // Story 35.4 — Batch 3 AI scoring expansion (from calls)
        ...aggregateAiExpansion(callRecords),
        // Story 35.4 — Batch 4 LeadReactors + Integrations
        ...aggregateLeadReactors(triggerRecords),
        ...aggregateIntegrations(integrationRecords),
      };
      allRecords.push(aggregate);

      // Emit a structured log with PII-redacted record counts only —
      // NEVER log per-record content per Round 3 Red Team Attack #1.
      emitStructuredLog("ctm_fetch_metrics_complete", {
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        accountId,
        region: metadata.region,
        callsCount: callRecords.length,
        formsCount: formRecords.length,
        smsCount: smsRecords.length,
        triggersCount: triggerRecords.length,
        integrationsCount: integrationRecords.length,
        durationMs: Date.now() - startedAt,
        warningCount: warnings.length,
      });

      return adapterOk({
        platform: PLATFORM as Platform,
        accountId,
        dateRange,
        records: allRecords,
        fetchedAt: new Date(),
        recordCount: allRecords.length,
        ...(warnings.length > 0 ? { warnings } : {}),
        metadata: {
          dataFreshnessLagHours: CTM_FRESHNESS_LAG_HOURS,
          region: metadata.region,
          conversationIntelligenceEntitled:
            metadata.conversationIntelligenceEntitled,
          leadReactorsEnabled,
          scorecardsEnabled,
        },
      });
    } catch (err) {
      if (err instanceof CtmApiCallError) {
        return adapterErr({
          code:
            err.type === "unauthorized"
              ? "token_expired"
              : err.type === "forbidden"
              ? "permission_denied"
              : err.type === "rate_limit_exceeded"
              ? "rate_limited"
              : err.type === "not_found"
              ? "account_not_found"
              : "provider_unavailable",
          message: err.message,
          retryable: err.retryable,
          providerStatus: err.status,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message:
          err instanceof Error ? err.message : "CTM fetchMetrics failed",
        retryable: true,
      });
    }
  },

  getAvailableMetrics(): MetricDefinition[] {
    return [...CTM_METRICS];
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return [...CTM_DIMENSIONS];
  },

  async discoverEndpoints(ctx, credentials) {
    void credentials;
    // Story 22.4 Code Review Round 1 — Critical #1: fixture-mode short-circuit
    // MUST also fire here, otherwise readCtmMetadata reads the production
    // `ctm:meta:{integrationId}` KV side-channel even when ctx.fixtures: true.
    // A tenant with a previously-connected real CTM integration (with
    // conversationIntelligenceEntitled: false cached) flipping into demo mode
    // would render the Likelihood Quality Pie's "subscription_required" badge
    // — violating Round 4 ADR-D entitlement-force-stamp invariant AND Round 3
    // Red Team Attack #2 "production ctm:meta:* prefix never touched" guard.
    //
    // Code Review Round 2 Edge Hunter #2 — emit drift-detection log here too
    // so all 4 adapter methods have symmetric truthy-non-true diagnostics.
    const rawFixtures = (ctx as unknown as { fixtures?: unknown }).fixtures;
    if (rawFixtures !== undefined && rawFixtures !== true && rawFixtures !== false) {
      emitStructuredLog("ctm_fixtures_flag_unexpected_type", {
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        method: "discoverEndpoints",
        actualType: typeof rawFixtures,
        actualValue: String(rawFixtures).slice(0, 50),
      });
    }
    const fixturesEnabled = rawFixtures === true;
    if (fixturesEnabled) {
      // Emit observability log so demo-mode invocations are traceable
      // (parity with loadCtmFixture's ctm_demo_fixture_loaded event).
      emitStructuredLog("ctm_demo_discover_endpoints_fixture", {
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
      });
      const info: EndpointDiscoveryInfo = {
        endpoints: [
          { key: "calls-list", status: "available" },
          { key: "form-submissions", status: "available" },
          { key: "sms", status: "available" },
          { key: "sale-inline", status: "available" },
          // Force CI entitled in fixture mode — mirrors loadCtmFixture
          // metadata force-stamp (Round 4 ADR-D).
          { key: "conversation-intelligence", status: "available" },
          // Story 35.4 — force entitled in fixture mode.
          { key: "lead-reactors", status: "available" },
          { key: "scorecards", status: "available" },
          { key: "integrations", status: "available" },
        ],
      };
      return adapterOk(info);
    }

    const metadata = await readCtmMetadata(ctx);
    const ciEntitled = metadata.conversationIntelligenceEntitled;
    const leadReactorsEnabled = metadata.leadReactorsEnabled === true;
    const scorecardsEnabled = metadata.scorecardsEnabled === true;
    const info: EndpointDiscoveryInfo = {
      endpoints: [
        { key: "calls-list", status: "available" },
        { key: "form-submissions", status: "available" },
        { key: "sms", status: "available" },
        { key: "sale-inline", status: "available" },
        {
          key: "conversation-intelligence",
          status: ciEntitled ? "available" : "subscription_required",
          ...(ciEntitled
            ? {}
            : { subscriptionRequired: "CTM Conversation Intelligence add-on" }),
        },
        // Story 35.4 — Batch 4 NEW endpoint families.
        {
          key: "lead-reactors",
          status: leadReactorsEnabled ? "available" : "subscription_required",
          ...(leadReactorsEnabled
            ? {}
            : { subscriptionRequired: "CTM LeadReactors add-on" }),
        },
        {
          key: "scorecards",
          status: scorecardsEnabled ? "available" : "subscription_required",
          ...(scorecardsEnabled
            ? {}
            : { subscriptionRequired: "CTM Scorecards add-on" }),
        },
        // Integrations is always available — even base-plan customers
        // see their connected 3rd-party partners.
        { key: "integrations", status: "available" },
      ],
    };
    return adapterOk(info);
  },
};

// ============================================================================
// Re-exports (per AC #11 public surface)
// ============================================================================

export { sanitizeCtmPii };
export { discoverCtmAccounts, validateCtmConnection, probeCtmConversationIntelligence };
